import { Box, BoxProps, Collapse } from '@chakra-ui/react';
import { useState } from 'react';

import { EmployerPhoneLookupForm, EmploymentStatusEnum } from '../gql/generated/graphql';
import { BaseEmploymentFormValues } from '../pages/LoanApp/utils';
import { getTimeAtLabel } from '../utils/customer';
import EmployerNameAndPhoneInputs, { EmployerLookupHandle } from './EmployerNameAndPhoneInputs';
import TextWithTooltip from './TextWithTooltip';
import { Label } from './formComponents';
import Input from './formComponents/Input';
import MonthsAndYears, { shouldRequirePrevious } from './monthsAndYears/MonthsAndYears';

export type TimeAtEmploymentValues = {
  [K in keyof BaseEmploymentFormValues]: BaseEmploymentFormValues[K];
} & {
  [K in keyof BaseEmploymentFormValues as `cobuyer_${K}`]?: BaseEmploymentFormValues[K];
};

interface TimeAtEmploymentProps {
  values: TimeAtEmploymentValues;
  isCobuyer?: boolean;
  firstName?: string;
  registerLookup?: (handle: EmployerLookupHandle) => () => void;
}

const TimeAtEmployment = ({
  values,
  isCobuyer = false,
  firstName,
  registerLookup,
  ...props
}: TimeAtEmploymentProps & BoxProps) => {
  const prefix = isCobuyer ? 'cobuyer_' : '';
  const years = values[`${prefix}years_at_job`];
  const months = values[`${prefix}months_at_job`] ?? 0;
  const [showPreviousEmployment, setShowPreviousEmployment] = useState<boolean>(
    shouldRequirePrevious(years, months),
  );

  const lookupForm = isCobuyer
    ? EmployerPhoneLookupForm.CobuyerPrevious
    : EmployerPhoneLookupForm.BuyerPrevious;

  return (
    <Box {...props}>
      <Label mb={3}>
        {getTimeAtLabel(values[`${prefix}employment_status`] as EmploymentStatusEnum)}
      </Label>
      <MonthsAndYears
        yearsName={`${prefix}years_at_job`}
        monthsName={`${prefix}months_at_job`}
        setShow={setShowPreviousEmployment}
        values={values}
        mb={showPreviousEmployment ? 4 : 0}
      />
      <Collapse in={showPreviousEmployment}>
        <Box
          w="100%"
          mb={2}
          p="8px"
          borderRadius="xl"
          bgColor="beige"
          fontSize={{ base: '10px', md: '12px' }}
        >
          {isCobuyer && firstName ? (
            <>
              {firstName} has been here for <b>less than two years</b>, where did they work before?{' '}
            </>
          ) : (
            <>
              You've been here for <b>less than two years</b>. Where did you work before?{' '}
            </>
          )}
          <TextWithTooltip
            textProps={{
              fontSize: { base: '10px', md: '12px' },
              cursor: showPreviousEmployment ? 'help' : 'default',
            }}
            label="To assess risk and establish a regular history of income, lenders prefer looking at more than two years' worth of data."
          >
            (Why does this matter?)
          </TextWithTooltip>
        </Box>
        <Box>
          <EmployerNameAndPhoneInputs
            form={lookupForm}
            nameField={`${prefix}previous_employer_name`}
            phoneField={`${prefix}previous_employer_phone_number`}
            phoneMask="999-999-9999"
            mt={4}
            mb={4}
            registerLookup={registerLookup}
          />
          <Input label="PREVIOUS JOB TITLE" name={`${prefix}previous_job_title`} />
          <Label mt={6} mb={3}>
            TIME AT PREVIOUS EMPLOYER?
          </Label>
          <MonthsAndYears
            yearsName={`${prefix}previous_years_at_job`}
            monthsName={`${prefix}previous_months_at_job`}
            mb={12}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default TimeAtEmployment;
